import { ref } from "vue";
import constant from "../const";
import consoleController from "@/utils/logUtil";

/**
 * グローバルステート管理
 *
 * リアクティブ変数を用いてアプリケーション全体の状態を管理する。
 * ブラウザのタブを閉じた場合や、リロードで状態はリセットされる。
 */

/**
 * 現在の国設定
 * 対応国: jp, ko, hk, tw
 * デフォルト: jp
 */
const currentCountry = ref(constant.DEFAULT_COUNTRY);

/**
 * 現在の地域設定
 * 対応地域: ja, ko, zh-HK, zh-TW, en
 * デフォルト: ja
 */
const currentLocale = ref(constant.DEFAULT_LOCALE);

/**
 * 利用可能な地域リスト
 */
const availableLocales = [
  { code: "ja", country: "", urlLang: "", ymkLang: "jpn" },
  { code: "ko", country: "ko", urlLang: "ko", ymkLang: "kor" },
  { code: "zh-HK", country: "hk", urlLang: "hk", ymkLang: "cht" },
  { code: "zh-TW", country: "tw", urlLang: "tw", ymkLang: "cht" },
  { code: "en", country: "", urlLang: "en", ymkLang: "enu" },
];

/**
 * 国コードが有効かチェック
 *
 * @param {string} country - 国コード
 * @returns {boolean}
 */
function isValidCountry(country) {
  return availableLocales.some((l) => l.country === country);
}

/**
 * 地域が有効かチェック
 *
 * @param {string} locale - 地域コード
 * @returns {boolean}
 */
function isValidLocale(locale) {
  return availableLocales.some(({ code }) => code === locale);
}

/**
 * 国コードから地域コードを取得
 *
 * @params {string} country - 国コード (ko, tw, hk)
 * @returns {string} 地域コード
 */
function getLocaleByCountry(country) {
  const locale = availableLocales.find((l) => l.country === country);
  return locale ? locale.code : constant.DEFAULT_LOCALE;
}

/**
 * 地域を変更する
 *
 * @param {string} locale - 地域コード (ja, ko, zh-HK, zh-TW, en)
 */
function setLocale(locale) {
  consoleController("log", `setLocale(${locale})`);
  if (isValidLocale(locale)) {
    currentLocale.value = locale;
  } else {
    currentLocale.value = constant.DEFAULT_LOCALE;
  }
}

/**
 * ブラウザの言語が英語かチェックする
 *
 * @returns {boolean}
 */
function isBrowserLanguageEnglish() {
  const browserLang = navigator.language || navigator.languages?.[0] || "";
  consoleController("log", `browserLang: ${browserLang}`);

  return browserLang.toLocaleLowerCase().startsWith("en");
}

/**
 * URLのクエリパラメータから地域を初期化
 *
 * @param {string?} country - 国コード
 * @param {string?} lang - 言語コード
 */
//  * @param {URLSearchParams|Object} params - URLSearchParams または route.query
function initLocaleFromQuery(country, lang) {
  consoleController("log", `country: ${country}, lang: ${lang}`);

  if (isValidCountry(country)) {
    let locale = getLocaleByCountry(country);
    if (locale) {
      currentCountry.value = country;

      // 香港の場合、英語指定可
      if (country === "hk") {
        // 第1優先：クエリパラメータが"英語"
        if (lang === "en") {
          locale = "en";
        }
        // 第2優先：ブラウザの言語が英語
        else if (isBrowserLanguageEnglish()) {
          locale = "en";
        }
        // それ以外は中国語繫体字
      }
      currentLocale.value = locale;
    }
  }

  // countryパラメータがないまたは無効はデフォルトを使用
}

/**
 * 肌分析SDKに設定する言語を取得
 *
 * @returns {string} 言語コード
 */
function getYmkLang() {
  const locale = availableLocales.find(
    ({ code }) => code === currentLocale.value,
  );
  return locale.ymkLang;
}

/**
 * URLに使用する言語コードを取得
 *
 * @param {string} locale - 地域コード (ja, ko, zh-HK, zh-TW, en)
 * @returns {string} 言語コード
 */
function getUrlLang(locale = null) {
  const localeCode = locale || currentLocale.value;
  const targetLocale = availableLocales.find(({ code }) => code === localeCode);
  return targetLocale.urlLang;
}

export default {
  currentCountry,
  currentLocale,
  setLocale,
  initLocaleFromQuery,
  getYmkLang,
  getUrlLang,
  getLocaleByCountry,
};
